import { useEffect, useId, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { CircleCheck, KeyRound, QrCode, RefreshCw } from 'lucide-react';
import { Abas, PainelAba } from '../../componentes/abas';
import { Botao } from '../../componentes/base';
import { EntradaSensivel, Rotulo } from '../../componentes/formulario';
import { CodigoQr } from '../../componentes/qr';
import { useToast } from '../../componentes/toast';
import { ErroRequisicao, get, mensagemDeErro, post } from '../../nucleo/api';
import { dataHora, telefoneFormatado } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import type { Conector, EstadoPareamento } from './tipos';

/** Fase 2 (W30): aviso de risco dos provedores não oficiais (cartão e pareamento). */
export function AvisoNaoOficial() {
  const { t } = useTranslation('integracoes');
  return (
    <p role="note" className="rounded-lg border border-laranja px-3 py-2 text-xs text-tinta-2">
      {t('conectores.whatsapp.aviso')}
    </p>
  );
}

/** Número pareado legível (no modo discreto, só os 4 últimos dígitos). */
export const numeroPareado = (numero: string | null | undefined) => (numero ? telefoneFormatado(`+${numero}`) : '');

const ESPERANDO: EstadoPareamento['estado'][] = ['iniciando', 'aguardando_qr', 'sem_sessao'];
/** Motivos que o gateway manda (protocolo da W30); outro valor não aparece na tela. */
// W40: versão recusada, falha de conexão e conta restrita (463).
const MOTIVOS = new Set(['qr_expirado', 'conexao_substituida', 'aparelho_removido', 'numero_bloqueado', 'bloqueio_temporario', 'reconectando', 'queda', 'sessao_removida', 'desconectada', 'versao_desatualizada', 'falha_conexao', 'conta_restrita']);

/**
 * Fase 2 (W30): pareamento do WhatsApp não oficial no Drawer do conector. Abre o pareamento no gateway (QR), consulta o
 * estado a cada 3 s enquanto espera a leitura e oferece o código de pareamento pelo número como alternativa ao QR.
 */
export function PareamentoWhatsApp({ conector, aoConcluir }: { conector: Conector; aoConcluir: () => void }) {
  const { t } = useTranslation('integracoes');
  const s = useSessao();
  const toast = useToast();
  const qc = useQueryClient();
  const idTelefone = useId();
  const idModo = useId();
  const [modo, setModo] = useState<'qr' | 'codigo'>('qr');
  const [telefone, setTelefone] = useState('');
  const [erroTelefone, setErroTelefone] = useState<string | null>(null);
  const [inicial, setInicial] = useState<EstadoPareamento | null>(null);

  const parear = useMutation({
    mutationFn: (corpo: { telefone?: string }) => post<EstadoPareamento>(`/integracoes/conectores/${conector.tipo}/parear`, corpo),
    onSuccess: (r) => {
      setInicial(r);
      setErroTelefone(null);
      void qc.invalidateQueries({ queryKey: ['integracoes', 'pareamento', conector.tipo] });
    },
    onError: (e) => {
      if (e instanceof ErroRequisicao && e.porCampo().telefone) setErroTelefone(e.porCampo().telefone!);
      else toast.erro(mensagemDeErro(e));
    },
  });
  // Abre o pareamento (QR) ao abrir o Drawer, uma vez só (o efeito roda duas vezes no modo estrito do React).
  const abriu = useRef(false);
  useEffect(() => {
    if (abriu.current) return;
    abriu.current = true;
    parear.mutate({});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const estado = useQuery({
    queryKey: ['integracoes', 'pareamento', conector.tipo],
    queryFn: () => get<EstadoPareamento>(`/integracoes/conectores/${conector.tipo}/pareamento`),
    enabled: !!inicial,
    refetchInterval: (q) => (q.state.data && !ESPERANDO.includes(q.state.data.estado) ? false : 3000),
  });
  const atual = estado.data ?? inicial;
  const pareado = atual?.estado === 'pareado';

  useEffect(() => {
    if (!pareado) return;
    toast.sucesso(t('conectores.whatsapp.pareadoToast'));
    void qc.invalidateQueries({ queryKey: ['integracoes', 'conectores'] });
  }, [pareado]); // eslint-disable-line react-hooks/exhaustive-deps

  const codigo = atual?.codigo ?? null;
  const motivo = atual?.motivo && MOTIVOS.has(atual.motivo) ? atual.motivo : null;
  return (
    <div className="flex flex-col gap-4" data-testid="pareamento-whatsapp">
      <p className="text-sm text-tinta-3">{t(`conectores.tipos.${conector.tipo}.descricao`)}</p>
      <AvisoNaoOficial />

      {pareado ? (
        <div role="status" className="flex flex-col items-start gap-3">
          <p className="flex items-center gap-2 text-sm font-medium text-tinta-1">
            <CircleCheck className="size-5 text-verde" aria-hidden />
            {t('conectores.whatsapp.pareadoCom', { numero: numeroPareado(atual?.numero) })}
          </p>
          <Botao variante="primario" onClick={aoConcluir}>
            {t('conectores.whatsapp.concluir')}
          </Botao>
        </div>
      ) : (
        <>
          {/* Review r20 spec (O3) e r19 visual (M1): o seletor de modo é a aba segmentada do núcleo (`Abas`), com o indicador de
              estado de 2 px em acento-forte (3:1 nos dois temas) e roving tabindex; no modo "Pelo código", o único primário é
              "Gerar código". */}
          <Abas
            variante="segmentado"
            idBase={idModo}
            rotulo={t('conectores.whatsapp.modo')}
            ativa={modo}
            aoMudar={setModo}
            itens={(
              [
                ['qr', QrCode, 'porQr'],
                ['codigo', KeyRound, 'porCodigo'],
              ] as const
            ).map(([chave, Icone, rotulo]) => ({
              chave,
              rotulo: (
                <span className="inline-flex items-center gap-2">
                  <Icone className="size-4" aria-hidden />
                  {t(`conectores.whatsapp.${rotulo}`)}
                </span>
              ),
            }))}
          />

          {atual && !ESPERANDO.includes(atual.estado) && (
            <p role="alert" className="rounded-lg border border-perigo px-3 py-2 text-sm text-perigo">
              {t(`conectores.whatsapp.estados.${atual.estado}`, { defaultValue: atual.estado })}
              {motivo ? ` (${t(`conectores.whatsapp.motivos.${motivo}`)})` : ''}
              {atual.ate_ms ? ` ${t('conectores.whatsapp.ate', { quando: dataHora(new Date(atual.ate_ms)) })}` : ''}
            </p>
          )}

          <PainelAba idBase={idModo} chave={modo}>
            {modo === 'qr' ? (
              <div className="flex flex-col gap-3 text-sm text-tinta-2">
                <ol className="flex list-decimal flex-col gap-1 pl-5">
                  <li>{t('conectores.whatsapp.passo1')}</li>
                  <li>{t('conectores.whatsapp.passo2')}</li>
                  <li>{t('conectores.whatsapp.passo3')}</li>
                </ol>
                {atual?.qr ? (
                  <CodigoQr texto={atual.qr} rotulo={t('conectores.whatsapp.qrRotulo')} tema={s.usuario.tema} />
                ) : (
                  <p role="status" className="text-tinta-3">
                    {parear.isPending || atual?.estado === 'iniciando' || !atual ? t('conectores.whatsapp.preparando') : t('conectores.whatsapp.semQr')}
                  </p>
                )}
                <p className="text-xs text-tinta-3">{t('conectores.whatsapp.qrRenova')}</p>
              </div>
            ) : (
              <form
                className="flex flex-col gap-3"
                noValidate
                onSubmit={(e) => {
                  e.preventDefault();
                  parear.mutate({ telefone });
                }}
              >
                <Rotulo id={idTelefone} rotulo={t('conectores.whatsapp.telefone')} erro={erroTelefone ?? undefined} ajuda={t('conectores.whatsapp.telefoneAjuda')}>
                  {/* Review r20 spec (M1): telefone é dado estruturado do modo discreto; sem foco, com o modo ligado, vira o marcador. */}
                  <EntradaSensivel mascara="telefone" id={idTelefone} inputMode="tel" autoComplete="off" className="numero" value={telefone} onChange={(e) => setTelefone(e.target.value)} />
                </Rotulo>
                <Botao type="submit" variante="primario" className="w-fit" carregando={parear.isPending}>
                  {t('conectores.whatsapp.gerarCodigo')}
                </Botao>
                {codigo && (
                  <div className="flex flex-col gap-1 text-sm text-tinta-2">
                    <span>{t('conectores.whatsapp.codigoInstrucao')}</span>
                    <code className="numero w-fit rounded-lg border border-borda bg-sup-2 px-3 py-2 text-lg tracking-widest text-tinta-1" aria-label={t('conectores.whatsapp.codigoRotulo')}>
                      {codigo}
                    </code>
                  </div>
                )}
              </form>
            )}
          </PainelAba>

          <div className="flex justify-end gap-2 border-t border-borda pt-4">
            <Botao variante="fantasma" onClick={aoConcluir}>
              {t('conectores.whatsapp.fechar')}
            </Botao>
            <Botao icone={<RefreshCw className="size-4" aria-hidden />} carregando={parear.isPending} onClick={() => parear.mutate({})}>
              {t('conectores.whatsapp.novoQr')}
            </Botao>
          </div>
        </>
      )}
    </div>
  );
}
